import React, { useState, useEffect, useRef } from "react";
import "./AdvisorBoard.css";
import { backendDomain } from "../utils";

const AdvisorBoard = () => {
  const [advisors, setAdvisors] = useState({});
  const [question, setQuestion] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAdvisor, setSelectedAdvisor] = useState(null);
  const [showCollaborativeAnalysis, setShowCollaborativeAnalysis] =
    useState(false);
  const [collaborativeAnalysis, setCollaborativeAnalysis] = useState(null);
  const [multiAgentConversation, setMultiAgentConversation] = useState(null);
  const [speakingAdvisor, setSpeakingAdvisor] = useState(null);
  const [expandedAnalysis, setExpandedAnalysis] = useState({});
  const [userContext, setUserContext] = useState("");
  const [isEditingContext, setIsEditingContext] = useState(false);
  const chatEndRef = useRef(null);

  // Fetch advisors on component mount
  useEffect(() => {
    fetchAdvisors();
  }, []);

  // Auto-scroll to bottom of chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatHistory]);

  const fetchAdvisors = async () => {
    try {
      const response = await fetch(
        `${backendDomain()}/api/advisor-board/advisors`
      );
      const data = await response.json();
      setAdvisors(data.advisors);
    } catch (error) {
      console.error("Error fetching advisors:", error);
    }
  };

  const askAdvisor = async (advisorType) => {
    if (!question.trim()) return;

    setIsLoading(true);
    setSpeakingAdvisor(advisorType);

    try {
      const response = await fetch(
        `${backendDomain()}/api/advisor-board/advice`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            question,
            advisorType,
            userContext: userContext.trim(),
            chatHistory: chatHistory.map((msg) => ({
              advisor: msg.advisor.name,
              message: msg.message,
            })),
          }),
        }
      );

      const data = await response.json();

      const newMessage = {
        id: Date.now(),
        advisor: data.advisor,
        message: data.advice,
        timestamp: new Date().toISOString(),
        type: "advisor",
      };

      setChatHistory((prev) => [...prev, newMessage]);
      setQuestion("");

      // Keep speaking state for a moment to show the effect
      setTimeout(() => {
        setSpeakingAdvisor(null);
      }, 2000);
    } catch (error) {
      console.error("Error getting advice:", error);
      setSpeakingAdvisor(null);
    } finally {
      setIsLoading(false);
    }
  };

  const getCollaborativeAnalysis = async () => {
    if (!question.trim()) return;

    setIsLoading(true);
    setShowCollaborativeAnalysis(true);
    try {
      const response = await fetch(
        `${backendDomain()}/api/advisor-board/collaborative-analysis`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            question,
            userContext: userContext.trim(),
            chatHistory: chatHistory.map((msg) => ({
              advisor: msg.advisor.name,
              message: msg.message,
            })),
          }),
        }
      );

      const data = await response.json();
      setCollaborativeAnalysis(data.analysis);

      const newMessage = {
        id: Date.now(),
        advisor: {
          name: "Team Synthesis",
          role: "Collaborative Analysis",
          avatar: "TS",
        },
        message: "Collaborative analysis completed. See details below.",
        timestamp: new Date().toISOString(),
        type: "analysis",
      };

      setChatHistory((prev) => [...prev, newMessage]);
      setQuestion("");
    } catch (error) {
      console.error("Error getting collaborative analysis:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getMultiAgentConversation = async () => {
    if (!question.trim()) return;

    setIsLoading(true);
    setShowCollaborativeAnalysis(true);
    try {
      const response = await fetch(
        `${backendDomain()}/api/advisor-board/multi-agent-conversation`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            question,
            maxRounds: 3,
            includeFollowUps: true,
          }),
        }
      );

      const data = await response.json();
      setMultiAgentConversation(data);

      const newMessage = {
        id: Date.now(),
        advisor: {
          name: "Multi-Agent Team",
          role: "Dynamic Conversation",
          avatar: "MA",
        },
        message:
          "Multi-agent conversation completed. See detailed discussion below.",
        timestamp: new Date().toISOString(),
        type: "multi-agent",
      };

      setChatHistory((prev) => [...prev, newMessage]);
      setQuestion("");
    } catch (error) {
      console.error("Error getting multi-agent conversation:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const clearChat = () => {
    setChatHistory([]);
    setCollaborativeAnalysis(null);
    setMultiAgentConversation(null);
    setShowCollaborativeAnalysis(false);
  };

  const toggleAnalysisSection = (section) => {
    setExpandedAnalysis((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const saveContext = () => {
    setIsEditingContext(false);
  };

  const clearContext = () => {
    setUserContext("");
    setIsEditingContext(false);
  };

  const startEditingContext = () => {
    setIsEditingContext(true);
  };

  return (
    <div className="advisor-board">
      <div className="advisor-board-header">
        <h1>AI Advisory Board</h1>
        <p>Get expert insights from our diverse consulting team</p>
      </div>

      <div className="advisor-board-content">
        {/* Context Section */}
        <div className="context-section">
          <div className="context-header">
            <div className="context-title">
              <h3>Your Situation</h3>
            </div>
            {!isEditingContext && (
              <div className="context-actions">
                {userContext ? (
                  <>
                    <button
                      onClick={startEditingContext}
                      className="edit-context-button"
                    >
                      Edit
                    </button>
                    <button
                      onClick={clearContext}
                      className="clear-context-button"
                    >
                      Clear
                    </button>
                  </>
                ) : (
                  <button
                    onClick={startEditingContext}
                    className="add-context-button"
                  >
                    Add Context
                  </button>
                )}
              </div>
            )}
          </div>

          {isEditingContext ? (
            <div className="context-edit-mode">
              <div className="context-edit-header">
                <h4>Tell us about your situation</h4>
                <p>Help advisors give better advice by sharing your context</p>
              </div>
              <textarea
                value={userContext}
                onChange={(e) => setUserContext(e.target.value)}
                placeholder="Describe your current situation, goals, challenges, or any relevant context that would help advisors give you better advice..."
                rows="3"
                autoFocus
                className="context-textarea"
              />
              <div className="context-edit-actions">
                <button onClick={saveContext} className="save-context-button">
                  Save Context
                </button>
                <button
                  onClick={() => setIsEditingContext(false)}
                  className="cancel-context-button"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : userContext ? (
            <div className="context-display">
              <p>{userContext}</p>
            </div>
          ) : (
            <div className="context-placeholder" onClick={startEditingContext}>
              <div className="placeholder-content">
                <p>Share your situation to get more personalized advice</p>
                <span className="placeholder-hint">Click to add context</span>
              </div>
            </div>
          )}
        </div>

        {/* Advisor Selection - Horizontal Compact */}
        <div className="advisors-selection">
          <div className="advisors-horizontal">
            {Object.entries(advisors).map(([key, advisor]) => (
              <div
                key={key}
                className={`advisor-compact ${
                  selectedAdvisor === key ? "selected" : ""
                } ${speakingAdvisor === key ? "speaking" : ""}`}
                onClick={() =>
                  setSelectedAdvisor(selectedAdvisor === key ? null : key)
                }
              >
                <div className="advisor-avatar-compact">{advisor.avatar}</div>
                <div className="advisor-info">
                  <h4>{advisor.name}</h4>
                  <p>{advisor.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="main-content">
          {/* Chat Interface */}
          <div className="chat-interface">
            <div className="chat-header">
              <h3>Discussion</h3>
              <button onClick={clearChat} className="clear-button">
                Clear Chat
              </button>
            </div>

            <div className="chat-messages">
              {chatHistory.length === 0 ? (
                <div className="welcome-message">
                  <p>Welcome to your AI Advisory Board!</p>
                  <p>
                    Ask a question below and get insights from our expert team.
                  </p>
                </div>
              ) : (
                chatHistory.map((message) => (
                  <div key={message.id} className={`message ${message.type}`}>
                    <div className="message-header">
                      <span className="advisor-avatar-small">
                        {message.advisor.avatar}
                      </span>
                      <span className="advisor-name">
                        {message.advisor.name}
                      </span>
                      <span className="message-time">
                        {new Date(message.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                    <div className="message-content">{message.message}</div>
                  </div>
                ))
              )}
              {isLoading && (
                <div className="message loading">
                  <div className="loading-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Input Section */}
            <div className="chat-input-section">
              <div className="input-container">
                <textarea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Ask your question here..."
                  rows="2"
                  disabled={isLoading}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      if (selectedAdvisor) {
                        askAdvisor(selectedAdvisor);
                      } else {
                        getCollaborativeAnalysis();
                      }
                    }
                  }}
                />
                <div className="input-actions">
                  {selectedAdvisor ? (
                    <button
                      onClick={() => askAdvisor(selectedAdvisor)}
                      disabled={!question.trim() || isLoading}
                      className="ask-button"
                    >
                      Ask {advisors[selectedAdvisor]?.name}
                    </button>
                  ) : (
                    <div className="team-analysis-buttons">
                      <button
                        onClick={getCollaborativeAnalysis}
                        disabled={!question.trim() || isLoading}
                        className="collaborative-button"
                      >
                        Team Analysis
                      </button>
                      <button
                        onClick={getMultiAgentConversation}
                        disabled={!question.trim() || isLoading}
                        className="multi-agent-button"
                      >
                        Multi-Agent
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="input-hint">
                {selectedAdvisor ? (
                  <p>
                    Asking {advisors[selectedAdvisor]?.name} for expert advice
                  </p>
                ) : (
                  <p>
                    Choose between quick team analysis or dynamic multi-agent
                    discussion
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Analysis Panel */}
          <div className="analysis-panel">
            {/* Collaborative Analysis Display */}
            {showCollaborativeAnalysis && collaborativeAnalysis && (
              <div className="collaborative-analysis">
                <div className="analysis-header">
                  <h4>Collaborative Analysis</h4>
                  <button
                    className="collapse-button"
                    onClick={() => toggleAnalysisSection("collaborative")}
                  >
                    {expandedAnalysis.collaborative ? "−" : "+"}
                  </button>
                </div>
                {expandedAnalysis.collaborative && (
                  <div className="analysis-content">
                    <div className="analysis-section">
                      <h5>Summary</h5>
                      <p>{collaborativeAnalysis.summary}</p>
                    </div>
                    <div className="analysis-section">
                      <h5>Key Insights</h5>
                      <p>{collaborativeAnalysis.keyInsights}</p>
                    </div>
                    <div className="analysis-section">
                      <h5>Recommendations</h5>
                      <p>{collaborativeAnalysis.recommendations}</p>
                    </div>
                    <div className="analysis-section">
                      <h5>Next Steps</h5>
                      <p>{collaborativeAnalysis.nextSteps}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Multi-Agent Conversation Display */}
            {showCollaborativeAnalysis && multiAgentConversation && (
              <div className="multi-agent-conversation">
                <div className="analysis-header">
                  <h4>Multi-Agent Conversation</h4>
                  <button
                    className="collapse-button"
                    onClick={() => toggleAnalysisSection("multiagent")}
                  >
                    {expandedAnalysis.multiagent ? "−" : "+"}
                  </button>
                </div>
                {expandedAnalysis.multiagent && (
                  <div className="analysis-content">
                    {/* Conversation Stats */}
                    <div className="conversation-stats">
                      <p>
                        <strong>Conversation Stats:</strong>{" "}
                        {multiAgentConversation.conversationStats.totalMessages}{" "}
                        messages across{" "}
                        {multiAgentConversation.conversationStats.totalRounds}{" "}
                        rounds with{" "}
                        {
                          multiAgentConversation.conversationStats
                            .participatingAgents
                        }{" "}
                        advisors
                      </p>
                    </div>

                    {/* Full Conversation */}
                    <div className="conversation-section">
                      <h5>Full Discussion</h5>
                      <div className="conversation-messages">
                        {multiAgentConversation.conversation.map(
                          (msg, index) => (
                            <div
                              key={index}
                              className={`conversation-message ${msg.type}`}
                            >
                              <div className="message-header">
                                <span className="advisor-avatar">
                                  {msg.avatar}
                                </span>
                                <span className="advisor-name">
                                  {msg.advisor}
                                </span>
                                <span className="advisor-role">
                                  ({msg.role})
                                </span>
                                <span className="message-round">
                                  Round {msg.round}
                                </span>
                              </div>
                              <div className="message-content">
                                {msg.message}
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </div>

                    {/* Analysis */}
                    <div className="analysis-section">
                      <h5>Summary</h5>
                      <p>{multiAgentConversation.analysis.summary}</p>
                    </div>
                    <div className="analysis-section">
                      <h5>Key Insights</h5>
                      <p>{multiAgentConversation.analysis.keyInsights}</p>
                    </div>
                    <div className="analysis-section">
                      <h5>Recommendations</h5>
                      <p>{multiAgentConversation.analysis.recommendations}</p>
                    </div>
                    <div className="analysis-section">
                      <h5>Next Steps</h5>
                      <p>{multiAgentConversation.analysis.nextSteps}</p>
                    </div>
                    <div className="analysis-section">
                      <h5>Conversation Highlights</h5>
                      <p>
                        {multiAgentConversation.analysis.conversationHighlights}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvisorBoard;
